import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map, mergeMap, first, ReplaySubject, shareReplay, switchMap, BehaviorSubject } from 'rxjs';
import { LobbyDataService } from './LobbyDataService';
import { NavigationService } from '@gatho/features/lobby/services/navigation.service';
import { RouteHelperService } from '@gatho/features/lobby/services/context/RouteHelperService';

@Injectable({ providedIn: 'root' })
export class ActiveDataService
{
  // The currently selected content type UUIDs
  private _areaUUID = new ReplaySubject<string | undefined>(1);
  private _siteUUID = new ReplaySubject<string | undefined>(1);
  private _siteTypeUUID = new ReplaySubject<string | undefined>(1);

  // Observables

  public activeContentType$ = new ReplaySubject<'area' | 'site' | 'siteType' | undefined>(1);
  public activeAreaUUID$ = this._areaUUID.asObservable();

  public activeArea$ = this._areaUUID.pipe(
    switchMap(uuid => this._dataService.allAreas$.pipe(
      map(areas => areas.find(area => area.uuid === uuid)),
    )),
    shareReplay(1),
  );

  public activeSite$ = this._siteUUID.pipe(
    filter(Boolean),
    switchMap(uuid => this._dataService.allSites$.pipe(
      mergeMap(sites => sites.filter(site => site.uuid === uuid)),
    )),
    shareReplay(1),
  );

  public activeSiteType$ = this._siteTypeUUID.pipe(
    filter(Boolean),
    switchMap(uuid => this._dataService.allSiteTypes$.pipe(
      mergeMap(types => types.filter(type => type.uuid === uuid)),
    )),
    shareReplay(1),
  );

  public activeRooms$ = this._areaUUID.pipe(
    filter(Boolean),
    switchMap(uuid => this._dataService.allRooms$.pipe(
      map(rooms => rooms.filter(room => room.areaId === uuid)),
    )),
    shareReplay(1),
  );

  // A helper to get the relevant observable from the type name
  public contentTypeObservableMap = {
    //room: this.activeRooms$,
    area: this.activeArea$,
    site: this.activeSite$,
    siteType: this.activeSiteType$
  }

  private _backLink?: string[];
  public backLink$ = new BehaviorSubject<string[] | undefined>(this._backLink);

  constructor(
    private _dataService: LobbyDataService,
    private _router: Router,
    private _navigationService: NavigationService,
    private routeHelperService: RouteHelperService
  ) {
    this.setActiveDataFromRoute();
    this.setParentDataFromChild();
    this.trackBackLink();
  }

  setActiveDataFromRoute() {
    this._router.events.subscribe((routerEvent) => {
      if (routerEvent instanceof NavigationEnd) {
        // Determine the active content based on route params
        const params = this.routeHelperService.getActiveRouteParams();
        // If a default nav level is set, that is the 'home' page and is always 'active'
        const defaultLevel = this._navigationService.defaultLevel;
        if (defaultLevel && !params[defaultLevel.type]) {
          params[defaultLevel.type] = defaultLevel.value.uuid;
        }
        if (params.area) {
          this._areaUUID.next(params.area);
          this.activeContentType$.next('area');
        }
        else if (params.site) {
          this._siteUUID.next(params.site);
          this.activeContentType$.next('site');
        }
        else if (params.siteType) {
          this._siteTypeUUID.next(params.siteType);
          this.activeContentType$.next('siteType');
        }
        else if (!Object.keys(params).length) {
          // If we went back to home or an arbitrary page
          // unset 'active' data
          this._areaUUID.next(undefined);
          this._siteUUID.next(undefined);
          this._siteTypeUUID.next(undefined);
          this.activeContentType$.next(undefined);
        }
      }
    });
  }

  setParentDataFromChild() {
    // If a path is visited directly (i.e: area)
    // make sure we set the 'parent' active data (site & site type)
    this.activeArea$.subscribe(area => {
      this._siteUUID.next(area?.siteUuid);
    });
    this.activeSite$.subscribe(site => {
      this._siteTypeUUID.next(site.siteTypeUuid);
    });
  }

  /**
   * @TODO make this less filthy
   */
  trackBackLink(): void {
    this.activeContentType$.subscribe((type) => {
      // Get the route to the content relevant to the current type
      if (type) {
        const activeContent$ = this.contentTypeObservableMap[type];
        // idk why theres an error on subscribe here
        // @ts-ignore
        activeContent$.pipe(first()).subscribe(activeItem => {
          let uuid = '';
          let parentType: 'area' | 'site' | 'siteType' | null = null;
          switch(type) {
            case 'area':
              uuid = activeItem.siteUuid;
              parentType = 'site';
            break;
            case 'site':
              uuid = activeItem.siteTypeUuid;
              parentType = 'siteType';
            break;
            case 'siteType':
              this.backLink$.next(['/']);
              return;
          }
          if (!parentType) {
            this.backLink$.next(undefined);
            return;
          }
          if (parentType === this._navigationService.defaultLevel?.type) {
            this.backLink$.next(['/']);
            return;
          }
          const backLink = [
            this._navigationService.routeTypeMap[parentType],
            uuid
          ];
          this._backLink = backLink;
          this.backLink$.next(this._backLink);
        });
      }
      else {
        this.backLink$.next(undefined);
      }
    });
  }
}
